import { css } from '@lastbrand/web-ui-styled-system/css'
import Image from 'next/image'

import { getErrorFromEvent } from '@src/quince-components/src/atoms/image/image.utils'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { isModifiedClickEvent } from '@src/utils/click-event.utils'

interface DesktopSubCollectionChipProps {
  imageUrl?: string
  label: string
  selected?: boolean
  onClick?: () => void
  'data-testid'?: string
  /** Subcollection URL. When set the chip renders as a crawlable anchor (SEO); plain clicks still filter in place. */
  href?: string
}

const CHIP_IMAGE_OVERLAY_SLOT = 'sub-collection-chip-image-overlay'

export function normalizeImageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined
  if (url.startsWith('//')) return `https:${url}`
  return url
}

export default function DesktopSubCollectionChip({
  imageUrl,
  label,
  onClick,
  selected = false,
  'data-testid': dataTestId,
  href,
}: DesktopSubCollectionChipProps) {
  const rootClassName = css({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'space-12',
    color: 'black',
    cursor: 'pointer',
    textDecoration: 'none',
    _hover: {
      [`& [data-slot="${CHIP_IMAGE_OVERLAY_SLOT}"]`]: {
        backgroundColor: 'darkgray-trans-10',
      },
    },
    _active: {
      [`& [data-slot="${CHIP_IMAGE_OVERLAY_SLOT}"]`]: {
        backgroundColor: 'darkgray-trans-20',
      },
    },
    _focusVisible: {
      outlineWidth: '2px',
      outlineOffset: '0.25rem',
      outlineColor: 'black',
      outlineStyle: 'solid',
    },
  })

  const chipContent = (
    <>
      <span
        className={css({
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '128px',
          height: '128px',
          borderRadius: 'radius-round',
          border: selected ? '1px solid' : 'none',
          borderColor: selected ? 'black' : 'transparent',
          padding: selected ? '3px' : '4px',
          overflow: 'hidden',
          bg: imageUrl ? 'white' : 'lightgray',
        })}
      >
        {imageUrl ? (
          <Image
            src={imageUrl}
            // The chip already exposes the visible label as its accessible name,
            // so this image is decorative and should not repeat the same text.
            alt=""
            width={120}
            height={120}
            sizes="120px"
            className={css({
              width: '120px',
              height: '120px',
              objectFit: 'cover',
              borderRadius: 'radius-round',
            })}
            onError={event => {
              captureException(getErrorFromEvent(event), {
                module: MODULES.IMAGE_RENDER,
                feature: FEATURES.IMAGE_WITH_SRC,
                extraInfo: {
                  src: imageUrl,
                  subCollectionLabel: label,
                },
              })
            }}
          />
        ) : (
          <span
            className={css({
              width: '120px',
              height: '120px',
              borderRadius: 'radius-round',
              bg: 'lightgray',
            })}
          />
        )}
        <span
          aria-hidden="true"
          data-slot={CHIP_IMAGE_OVERLAY_SLOT}
          className={css({
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            height: '120px',
            borderRadius: 'radius-round',
            backgroundColor: 'transparent',
            transition: 'background-color 150ms ease-out',
            pointerEvents: 'none',
          })}
        />
      </span>
      <span
        className={css({
          textStyle: selected ? 'body-14-strong' : 'body-14',
          minWidth: 'full',
          overflow: 'hidden',
          textAlign: 'center',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '120px',
        })}
      >
        {label}
      </span>
    </>
  )

  if (href) {
    return (
      <a
        href={href}
        aria-current={selected ? 'true' : undefined}
        data-testid={dataTestId}
        onClick={event => {
          // Modified clicks (new tab/window, middle-click) follow the crawlable href;
          // plain clicks keep the in-app filter/toggle navigation.
          if (isModifiedClickEvent(event)) return
          event.preventDefault()
          onClick?.()
        }}
        className={rootClassName}
      >
        {chipContent}
      </a>
    )
  }

  return (
    <button type="button" aria-pressed={selected} onClick={onClick} data-testid={dataTestId} className={rootClassName}>
      {chipContent}
    </button>
  )
}
